/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import * as moment from "moment";
import PulseLoader from "react-spinners/PulseLoader";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { handleApiError } from "@utils/errorHandler";
import OrderSummary from "./components/OrderSummary/OrderSummary";
import SupplierPlanning from "./components/SupplierPlanning";
import OrderPlanningHistory from "./components/OrderPlanningHistory";
import {
  SupplierListContainer,
  OuterWrapper,
  Title,
  GridRow,
  BreadCrumb,
} from "./SupplierList.styled";
import "./SupplierList.scss";
import NotFound from "@components/Errors/NotFound";

/**
 * site GeneralInfo,
 * Path - /planning/suppliers
 * @see See [https://hesusstore.atlassian.net/wiki/spaces/HS/pages/3320578053/Plannings)
 * @component
 */
const SupplierList = (props) => {
  const { t } = props;
  const history = useNavigate();
  const params = useParams();
  const location = useLocation();
  const isPlanningSuppliersPath = location.pathname.match(
    /^\/planning\/(\d+)\/suppliers$/
  );
  const clientOrderId = params.clientOrderId;
  const [isNotFound, setIsNotFound] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshDataAgain, setRefreshState] = useState(false);
  const [data, setData] = useState({});
  const [planningLines, setPlanningLines] = useState([]);
  const [planningFigures, setPlanningFigures] = useState([]);
  const [jobsiteCountryId, setJobsiteCountryId] = useState("");

  useEffect(() => {
    if (!clientOrderId) {
      history(`/planning/orders`);
    }
  }, [clientOrderId]);

  const fetchClientOrder = (id) => {
    return axios
      .get(`/clientOrders/${id}`)
      .then((response) => {
        setData(response.data?.[0]);
        setJobsiteCountryId(response.data?.[0].site.platformCountry.id);
        const orderData = moment(
          response.data?.[0]?.services[0]?.clientOrder?.planningDate,
          ).format("YYYY-MM-DD");
        localStorage.setItem("clientOrderDate", orderData);
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
           setIsNotFound(true);
           return;
        }
        handleApiError(error);
      });
  };

  const fetchPlanningLines = (id) => {
    return axios
      .get(`/clientOrders/${id}/suppliersPlanningLines`)
      .then((response) => {
        setPlanningLines(response.data?.supplierPlanningLine);
      })
      .catch((error) => handleApiError(error));
  };

  const fetchPlanningFigures = (id) => {
    return axios
      .get(`/clientOrders/${id}/planningFigures`)
      .then((response) => {
        setPlanningFigures(response.data?.[0]);
      })
      .catch((error) => handleApiError(error));
  };

  useEffect(() => {
    if (clientOrderId) {
      setIsLoading(true);
      Promise.all([
        fetchClientOrder(clientOrderId),
        fetchPlanningLines(clientOrderId),
        fetchPlanningFigures(clientOrderId),
      ]).finally(() => setIsLoading(false));
    }
  }, [clientOrderId]);

  useEffect(() => {
    if (isRefreshDataAgain) {
      setRefreshState(false);
      setIsLoading(true);
      Promise.all([
        fetchPlanningLines(clientOrderId),
        fetchPlanningFigures(clientOrderId),
      ]).finally(() => setIsLoading(false));
    }
  }, [isRefreshDataAgain]);

  if (!isPlanningSuppliersPath || isNotFound) {
    return <NotFound />;
  }

  return (
    <SupplierListContainer className="planning-order-list">
      <Title>{t("SUPPLIERS_PLANNING")}</Title>

      <OuterWrapper>
        <GridRow column="1fr">
          <BreadCrumb>
            <Link
              to={`/planning/orders`}
              className="supplier-list__supplier-link"
            >
              {"<"}
              {t("GENERAL_BACK_TO_CLIENTS_ORDERS")}
            </Link>
          </BreadCrumb>
        </GridRow>
        {isLoading && (
          <div className="suggestions-list-loading">
            <PulseLoader color="#1bace4" size={15} />
          </div>
        )}
        {!isLoading && (
          <>
            <OrderSummary
              orderData={data?.services?.[0]}
              quoteCurrency={data?.quoteCurrency}
              exchangeRates={planningFigures?.exchangeRates}
              site={data?.site}
            />
            <SupplierPlanning
              clientOrderId={clientOrderId}
              planningLines={planningLines}
              exchangeRates={planningFigures?.exchangeRates}
              quoteCurrency={data?.quoteCurrency}
              isLoading={isLoading}
              setRefreshState={setRefreshState}
              planningFigures={planningFigures}
              jobsiteCountryId={jobsiteCountryId}
            />
            {data?.services?.[0]?.id && (
              <OrderPlanningHistory
                quoteServiceId={data?.services?.[0]?.id}
                clientOrderId={clientOrderId}
                setRefreshState={setRefreshState}
                planningDate={data?.services?.[0]?.clientOrder?.planningDate}
              />
            )}
          </>
        )}
      </OuterWrapper>
    </SupplierListContainer>
  );
};
SupplierList.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation()(SupplierList);
